import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Factory } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import DesignChangeWorkbench from './DesignChangeWorkbench';
import CountermeasureWorkbench from './CountermeasureWorkbench';
import FailureModeWorkbench from './FailureModeWorkbench';
import DRBFMWorkflowStartStep from './DRBFMWorkflowStartStep';
import InputArtifactPanel from './InputArtifactPanel';
import StageRunPanel from './StageRunPanel';
import WorkflowProgressRail from './WorkflowProgressRail';
import FunctionPartMatrixWorkbench from './FunctionPartMatrixWorkbench';
import { useStagedWorkflow } from './useStagedWorkflow';

interface DRBFMStagedWorkflowProps {
  resumeSessionId?: string | null;
  onSession?: (sessionId: string) => void;
  /** Lets the page ask before a mode switch throws this run away. */
  onContentChange?: (hasContent: boolean) => void;
}

const DRBFMStagedWorkflow: React.FC<DRBFMStagedWorkflowProps> = ({ onContentChange, resumeSessionId, onSession }) => {
  const { t } = useTranslation();
  const {
    startValue,
    setStartValue,
    itemName,
    setItemName,
    workflow,
    sessionId,
    oldText,
    setOldText,
    newText,
    setNewText,
    changeReportText,
    setChangeReportText,
    oldCadModelId,
    setOldCadModelId,
    newCadModelId,
    setNewCadModelId,
    isLoading,
    error,
    reviewedCount,
    reviewedFailureModeCount,
    handleStart,
    handleExtractChanges,
    handleReviewChange,
    handleConfirmComparability,
    handleMatrixImport,
    handleMatrixPrefill,
    handleMatrixCellUpdate,
    handleMatrixPromotion,
    handleGenerateFailureModes,
    handleReviewFailureMode,
    handleGenerateCountermeasures,
    handleReviewCountermeasure,
    handleExport,
  } = useStagedWorkflow({ onContentChange, resumeSessionId, onSession });
  // A refusal on a lower card would otherwise land in an alert scrolled out of view.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [error]);

  return (
    <div className="flex flex-col gap-6">
      {error && <Alert ref={errorRef} variant="destructive">{error}</Alert>}
      {resumeSessionId && isLoading && !workflow && (
        <Spinner label={t('common.loading', '読み込み中')} />
      )}

      {!sessionId && !resumeSessionId && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="drbfm-item-name">{t('drbfm.workflow.staged.targetPartLabel', '対象部品')}</Label>
            <Input
              id="drbfm-item-name"
              value={itemName}
              onChange={(event) => setItemName(event.target.value)}
              disabled={isLoading}
            />
          </div>
          <DRBFMWorkflowStartStep
            value={startValue}
            isLoading={isLoading}
            onChange={setStartValue}
            onStart={handleStart}
          />
        </div>
      )}

      {workflow && (
        <div className="flex flex-col gap-6">
          <Alert
            role="status"
            variant={(workflow.readiness?.readiness || 'draft') === 'draft' ? 'warning' : 'success'}
          >
            {t('drbfm.workflow.staged.readinessStatus', '内容状態: {{readiness}} / 未解決 {{blockers}}件', {
              readiness: workflow.readiness?.readiness || 'draft',
              blockers: workflow.readiness?.blockers?.length || 0,
            })}
          </Alert>
          <Separator />
          <WorkflowProgressRail
            stageRuns={workflow.stageRuns}
            inputPattern={startValue.inputPattern}
          />
          <Separator />
          {startValue.inputPattern === 'function_part_matrix' ? (
            <FunctionPartMatrixWorkbench
              matrix={workflow.functionPartMatrix}
              isLoading={isLoading}
              onImport={handleMatrixImport}
              onPrefill={handleMatrixPrefill}
              onUpdateCell={handleMatrixCellUpdate}
              onPromote={handleMatrixPromotion}
            />
          ) : (
            <InputArtifactPanel
              inputPattern={startValue.inputPattern}
              oldText={oldText}
              newText={newText}
              changeReportText={changeReportText}
              oldCadModelId={oldCadModelId}
              newCadModelId={newCadModelId}
              isLoading={isLoading}
              onOldTextChange={setOldText}
              onNewTextChange={setNewText}
              onChangeReportTextChange={setChangeReportText}
              onOldCadModelIdChange={setOldCadModelId}
              onNewCadModelIdChange={setNewCadModelId}
              onExtractChanges={handleExtractChanges}
            />
          )}
          <Separator />
          <DesignChangeWorkbench
            changes={workflow.designChanges}
            isLoading={isLoading}
            onReviewChange={handleReviewChange}
            onConfirmComparability={handleConfirmComparability}
          />
          <Separator />
          <div className="flex justify-end">
            <Button
              disabled={isLoading || reviewedCount === 0}
              onClick={handleGenerateFailureModes}
            >
              {isLoading ? <Spinner size="sm" label="" /> : <Factory />}
              {t('drbfm.workflow.staged.proceedToFailureModes', '故障モード生成へ進む')}
            </Button>
          </div>
          <Separator />
          <FailureModeWorkbench
            failureModes={workflow.failureModes}
            isLoading={isLoading}
            onReview={handleReviewFailureMode}
          />
          <Separator />
          <div className="flex justify-end">
            <Button
              disabled={isLoading || reviewedFailureModeCount === 0}
              onClick={handleGenerateCountermeasures}
            >
              {t('drbfm.workflow.staged.generateCountermeasures', '対策候補を生成')}
            </Button>
          </div>
          <Separator />
          <CountermeasureWorkbench
            countermeasures={workflow.countermeasures}
            isLoading={isLoading}
            onReview={handleReviewCountermeasure}
          />
          <Separator />
          <div className="flex flex-row flex-wrap justify-end gap-2">
            <Button disabled={isLoading} variant="outline" onClick={() => handleExport('draft')}>
              {t('drbfm.workflow.staged.exportDraft', 'Draft出力')}
            </Button>
            <Button
              disabled={isLoading || !workflow.readiness?.allowedExportModes.includes('reviewed')}
              variant="outline"
              onClick={() => handleExport('reviewed')}
            >
              {t('drbfm.workflow.staged.exportReviewed', 'レビュー済み出力')}
            </Button>
            <Button
              disabled={isLoading || !workflow.readiness?.allowedExportModes.includes('scored')}
              onClick={() => handleExport('scored')}
            >
              {t('drbfm.workflow.staged.exportScored', '採点済み出力')}
            </Button>
          </div>
          <Separator />
          <StageRunPanel stageRuns={workflow.stageRuns} />
        </div>
      )}
    </div>
  );
};

export default DRBFMStagedWorkflow;
